import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const SalesReport = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // State for invoices and loading
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for customers list
  const [customers, setCustomers] = useState([]);

  // State for filters
  const [filters, setFilters] = useState({
    customerName: '',
    invoiceNumber: '',
    dateFrom: '',
    dateTo: '',
    status: '', // '' for all, 'Paid', 'Unpaid'
  });

  // State for filtered results
  const [filteredInvoices, setFilteredInvoices] = useState([]);

  // Fetch invoices and customers on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch invoices
        const invoicesResponse = await axios.get(`${apiUrl}/api/invoices`);
        const invoicesData = Array.isArray(invoicesResponse.data)
          ? invoicesResponse.data
          : invoicesResponse.data.data || [];
        setInvoices(invoicesData);
        setFilteredInvoices(invoicesData);

        // Fetch customers for dropdown
        const customersResponse = await axios.get(`${apiUrl}/api/customers`);
        const customersData = Array.isArray(customersResponse.data)
          ? customersResponse.data
          : customersResponse.data.data || [];
        setCustomers(customersData);

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load sales data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl]);

  // Apply filters whenever they change or invoices change
  useEffect(() => {
    applyFilters();
  }, [filters, invoices]);

  // Helper function to get customer name from nested object
  const getCustomerName = (invoice) => {
    return invoice.customer?.name || '';
  };

  // Helper function to calculate invoice totals from line items
  const calculateInvoiceTotals = (invoice) => {
    if (!invoice.ilines || invoice.ilines.length === 0) {
      return { subtotal: 0, tax: 0, total: 0 };
    }

    const subtotal = invoice.ilines.reduce((sum, line) => sum + (parseFloat(line.line_subtotal_after_discount) || 0), 0);
    const tax = invoice.ilines.reduce((sum, line) => sum + (parseFloat(line.line_vat) || 0), 0);
    const total = invoice.ilines.reduce((sum, line) => sum + (parseFloat(line.line_total) || 0), 0);

    return { subtotal, tax, total };
  };

  // Apply filters to invoices
  const applyFilters = () => {
    let filtered = [...invoices];

    // Filter by customer name
    if (filters.customerName) {
      filtered = filtered.filter((invoice) =>
        getCustomerName(invoice)
          .toLowerCase()
          .includes(filters.customerName.toLowerCase())
      );
    }

    // Filter by invoice number
    if (filters.invoiceNumber) {
      filtered = filtered.filter((invoice) =>
        invoice.invoice_no
          ?.toLowerCase()
          .includes(filters.invoiceNumber.toLowerCase())
      );
    }

    // Filter by date range
    if (filters.dateFrom) {
      filtered = filtered.filter((invoice) => {
        const invoiceDate = new Date(invoice.invoice_date);
        const fromDate = new Date(filters.dateFrom);
        return invoiceDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter((invoice) => {
        const invoiceDate = new Date(invoice.invoice_date);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // Include entire day
        return invoiceDate <= toDate;
      });
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(
        (invoice) => invoice.status === filters.status
      );
    }

    setFilteredInvoices(filtered);
  };

  // Handle filter input change
  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      customerName: '',
      invoiceNumber: '',
      dateFrom: '',
      dateTo: '',
      status: '',
    });
  };

  // Export table to PDF
  const exportToPDF = async () => {
    try {
      const pdfDocument = React.createElement(SalesReportPDFDocument);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SalesReport_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  // PDF Styles
  const pdfStyles = StyleSheet.create({
    page: {
      padding: 30,
      fontFamily: 'Helvetica',
    },
    title: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 15,
      color: '#0891b2',
    },
    subtitle: {
      fontSize: 10,
      color: '#666',
      marginBottom: 20,
    },
    table: {
      width: '100%',
      marginBottom: 20,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: '#f3f4f6',
      fontWeight: 'bold',
      fontSize: 9,
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingVertical: 6,
    },
    tableRow: {
      flexDirection: 'row',
      fontSize: 8,
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
      paddingVertical: 6,
    },
    tableTotalRow: {
      flexDirection: 'row',
      backgroundColor: '#f9fafb',
      fontWeight: 'bold',
      fontSize: 9,
      borderTopWidth: 2,
      borderTopColor: '#000',
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingVertical: 6,
    },
    col1: {
      width: '12%',
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    col2: {
      width: '18%',
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    col3: {
      width: '12%',
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    col4: {
      width: '14%',
      paddingHorizontal: 6,
      overflow: 'hidden',
      textAlign: 'right',
    },
    col5: {
      width: '14%',
      paddingHorizontal: 6,
      overflow: 'hidden',
      textAlign: 'right',
    },
    col6: {
      width: '14%',
      paddingHorizontal: 6,
      overflow: 'hidden',
      textAlign: 'right',
    },
    col7: {
      width: '16%',
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    footerText: {
      fontSize: 9,
      color: '#666',
      marginTop: 10,
    },
  });

  // PDF Document Component
  const SalesReportPDFDocument = () => {
    const hasDateFilter = filters.dateFrom || filters.dateTo;
    const dateRangeText = hasDateFilter
      ? `${filters.dateFrom ? formatDate(filters.dateFrom) : 'Start'} - ${filters.dateTo ? formatDate(filters.dateTo) : 'End'}`
      : '';

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          <Text style={pdfStyles.title}>Sales Report</Text>
          <View style={{ marginBottom: 20 }}>
            <Text style={pdfStyles.subtitle}>
              Generated on {new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </Text>
            {hasDateFilter && (
              <Text style={{ fontSize: 10, color: '#0891b2', fontWeight: 'bold', marginTop: 5 }}>
                Period: {dateRangeText}
              </Text>
            )}
          </View>

          <View style={pdfStyles.table}>
            {/* Table Header */}
            <View style={pdfStyles.tableHeader}>
              <Text style={pdfStyles.col1}>Invoice #</Text>
              <Text style={pdfStyles.col2}>Customer</Text>
              <Text style={pdfStyles.col3}>Date</Text>
              <Text style={pdfStyles.col4}>Subtotal</Text>
              <Text style={pdfStyles.col5}>Tax</Text>
              <Text style={pdfStyles.col6}>Total</Text>
              <Text style={pdfStyles.col7}>Status</Text>
            </View>

            {/* Table Rows */}
            {filteredInvoices.map((invoice) => {
              const invoiceTotals = calculateInvoiceTotals(invoice);
              return (
                <View key={invoice.id} style={pdfStyles.tableRow}>
                  <Text style={pdfStyles.col1}>{invoice.invoice_no}</Text>
                  <Text style={pdfStyles.col2}>{getCustomerName(invoice)}</Text>
                  <Text style={pdfStyles.col3}>{formatDate(invoice.invoice_date)}</Text>
                  <Text style={pdfStyles.col4}>{formatCurrency(invoiceTotals.subtotal)}</Text>
                  <Text style={pdfStyles.col5}>{formatCurrency(invoiceTotals.tax)}</Text>
                  <Text style={pdfStyles.col6}>{formatCurrency(invoiceTotals.total)}</Text>
                  <Text style={pdfStyles.col7}>{invoice.status}</Text>
                </View>
              );
            })}

            {/* Totals Row */}
            <View style={pdfStyles.tableTotalRow}>
              <Text style={pdfStyles.col1}></Text>
              <Text style={pdfStyles.col2}></Text>
              <Text style={pdfStyles.col3}></Text>
              <Text style={pdfStyles.col4}>{formatCurrency(totals.subtotal)}</Text>
              <Text style={pdfStyles.col5}>{formatCurrency(totals.tax)}</Text>
              <Text style={pdfStyles.col6}>{formatCurrency(totals.total)}</Text>
              <Text style={pdfStyles.col7}></Text>
            </View>
          </View>

          <Text style={pdfStyles.footerText}>
            Showing {filteredInvoices.length} of {invoices.length} invoices
          </Text>
        </Page>
      </Document>
    );
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount || 0);
  };

  // Calculate totals for filtered results
  const calculateTotals = () => {
    let subtotal = 0;
    let tax = 0;
    let total = 0;

    filteredInvoices.forEach((invoice) => {
      const invoiceTotals = calculateInvoiceTotals(invoice);
      subtotal += invoiceTotals.subtotal;
      tax += invoiceTotals.tax;
      total += invoiceTotals.total;
    });

    return { subtotal, tax, total };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading sales data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Sales Report</h1>
          <p className="text-gray-600 mt-2">
            View and search all sales invoices
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            {/* Customer Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer
              </label>
              <input
                type="text"
                placeholder="Search customer..."
                value={filters.customerName}
                onChange={(e) =>
                  handleFilterChange('customerName', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Invoice Number Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice #
              </label>
              <input
                type="text"
                placeholder="Search invoice..."
                value={filters.invoiceNumber}
                onChange={(e) =>
                  handleFilterChange('invoiceNumber', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date From Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="Paid">Paid</option>
                <option value="Unpaid">Unpaid</option>
              </select>
            </div>
          </div>

          {/* Reset Button */}
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
          >
            Reset Filters
          </button>

          {/* Export Button */}
          <button
            onClick={exportToPDF}
            disabled={filteredInvoices.length === 0}
            className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Export to PDF
          </button>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredInvoices.length} of {invoices.length} invoices
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {filteredInvoices.length > 0 ? (
            <>
              <table className="w-full">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Invoice #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Subtotal
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Tax
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredInvoices.map((invoice) => {
                    const invoiceTotals = calculateInvoiceTotals(invoice);
                    return (
                    <tr
                      key={invoice.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-blue-600">
                          {invoice.invoice_no}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getCustomerName(invoice)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(invoice.invoice_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        {formatCurrency(invoiceTotals.subtotal)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        {formatCurrency(invoiceTotals.tax)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                        {formatCurrency(invoiceTotals.total)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            invoice.status === 'Paid'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Totals Footer */}
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                <div className="flex justify-end gap-8 font-semibold">
                  <div className="text-gray-700">
                    Subtotal: {formatCurrency(totals.subtotal)}
                  </div>
                  <div className="text-gray-700">
                    VAT: {formatCurrency(totals.tax)}
                  </div>
                  <div className="text-lg text-gray-900">
                    Total: {formatCurrency(totals.total)}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-gray-500">
              No invoices found matching your filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesReport;
